/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react'
import {
  Container,
  Button,
  Icon,
  Image,
  ListContainer,
  Pagination,
  TextTitle,
  TextBody,
  Tools
} from '@pdrj/pmi-iqos-ds-react'
import { Assets, LayoutContainer } from '@pdrj/pmi-iqos-shared-react'
import SpotsUsageBadge from './spots-usage-badge'
import SpotsBusinessBadge from './spots-business-badge'
import SpotsFilters from './spots-filters'
import SpotServiceTypeBadge from './spots-service-type-badge'

const VenueCard = ({
  translations,
  spot,
  handleOpenVenueDetail,
  businessTypes,
  usageTypes,
  filters
}) => {
  const usageBadge = <SpotsUsageBadge spot={spot} usageTypes={usageTypes} />
  const serviceTypeBadge = (
    <SpotServiceTypeBadge
      tags={spot.serviceType}
      serviceTypes={filters.filter(i => i.type === 'serviceType')}
    />
  )
  return (
    <Container
      fullWidth
      fullHeight
      display={Container.displays.flex}
      flexDirection={Container.flexDirections.column}>
      <Container
        fullWidth
        px="4"
        pt="4"
        bg={Container.backgrounds.hardWhite}
        onClickHandler={handleOpenVenueDetail(spot)}>
        <Container
          pb={['4', '8']}
          display={Container.displays.flex}
          flexDirection={[
            Container.flexDirections.row,
            Container.flexDirections.column
          ]}
          divider={Tools.isDesktop() ? '' : Container.dividers.bottom}>
          <Container
            width={['2/6', 'full']}
            display={Container.displays.flex}
            alignItems={Container.alignItemsValues.start}
            justifyContent={Container.justifyContentValues.center}
            position={Container.positions.relative}>
            <Container position={Container.positions.absolute} t="0" l="0">
              <SpotsBusinessBadge
                imageWidth={Tools.isDesktop() ? '12' : '6'}
                spot={spot}
                businessTypes={businessTypes}
              />
            </Container>
            <Image
              width="full"
              cover
              src={spot.spotImage || Assets.urls.imageSpotDefault}
            />
          </Container>
          <Container
            width={['4/6', 'full']}
            pt={['0', '5']}
            pl={['4', '0']}
            display={Container.displays.flex}
            flexDirection={Container.flexDirections.column}>
            <Container onClickHandler={handleOpenVenueDetail(spot)}>
              <TextTitle
                ellipsis
                heading={TextTitle.headings.h6}
                data-testing={'SpotsListView_TextTitle_VenueCard'}>
                {spot.spotName}
              </TextTitle>
            </Container>
            <Container pt="2">
              <TextBody>
                {translations.tk_distanceFromLocation} {spot.distance}
                {translations.tk_km}
              </TextBody>
            </Container>
            <Container pt="4">
              <ListContainer
                xBetween="2"
                yBetween="2"
                columns={[1, null]}
                truncate={true}>
                {usageBadge}
                {serviceTypeBadge}
              </ListContainer>
            </Container>
          </Container>
        </Container>
      </Container>
    </Container>
  )
}

const SpotsListView = ({
  translations,
  spots,
  businessTypes,
  usageTypes,
  handleOpenVenueDetail,
  backToMap,
  filters,
  toggleFilterHandler
}) => {
  const itemsPage = !Tools.isDesktop() ? 8 : 16
  const maxDisplayedPageNumbers = !Tools.isDesktop() ? 6 : 10

  const [currentPage, setCurrentPage] = useState(0)
  const [pageAmount, setPageAmount] = useState(0)
  const [startIndex, setStartIndex] = useState(0)
  const [endIndex, setEndIndex] = useState(0)
  const [slicePagination, setSlicePagination] = useState(false)
  const diffPageNumbers = slicePagination
    ? pageAmount - maxDisplayedPageNumbers
    : null

  useEffect(() => {
    setPageAmount(Math.ceil(spots.length / itemsPage))
    setCurrentPage(0)
    setStartIndex(0)
  }, [spots])

  useEffect(() => {
    if (pageAmount > maxDisplayedPageNumbers) {
      setSlicePagination(true)
    }
    setEndIndex(pageAmount - diffPageNumbers)
  }, [pageAmount, diffPageNumbers, slicePagination])

  const paginate = i => {
    setCurrentPage(i)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const prevPaginate = () => {
    if (currentPage <= diffPageNumbers && startIndex > 0) {
      setStartIndex(startIndex - 1)
      setEndIndex(endIndex - 1)
    }
  }

  const nextPaginate = () => {
    if (
      currentPage >= maxDisplayedPageNumbers - 1 &&
      currentPage <= pageAmount &&
      startIndex < diffPageNumbers
    ) {
      setStartIndex(startIndex + 1)
      setEndIndex(endIndex + 1)
    }
  }

  const getCurrentSpots = (items, pageIndex, itemsPerpage) => {
    return items.slice(
      pageIndex * itemsPerpage,
      Math.min(items.length, (pageIndex + 1) * itemsPerpage)
    )
  }

  return (
    <>
      <LayoutContainer fullWidth xMainLayout={Tools.isDesktop()}>
        <Container pt={['4', '4', '8']} pl={['4']}>
          <Button
            icon={Icon.images.arrowLeft}
            iconPosition={Button.iconPositions.left}
            onClickHandler={() => {
              backToMap()
            }}
            text={translations.tk_spotListBackButton}
            variant={Button.variants.tertiary}
            data-testing="SpotsListView_Button_Back"
          />
        </Container>
      </LayoutContainer>
      <LayoutContainer fullWidth xMainLayout={Tools.isDesktop()}>
        <Container
          pt={['0', '4', '8']}
          pb={['8', '8', '20']}
          layout={Container.layouts.mid}
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.column}
          alignItems={Container.alignItemsValues.center}>
          <Container
            fullWidth
            pl="1"
            pb="4"
            display={Container.displays.flex}
            overflow={!Tools.isDesktop()}
            flexWrap={Tools.isDesktop()}>
            <SpotsFilters
              filters={filters}
              toggleFilterHandler={toggleFilterHandler}
              paddingLeft="2"
            />
          </Container>
          <ListContainer
            xBetween={['0', '4']}
            yBetween={['0', '4']}
            columns={[1, 2, 4]}
            fullWidth>
            {getCurrentSpots(spots, currentPage, itemsPage).map((spot, i) => (
              <VenueCard
                key={i}
                translations={translations}
                spot={spot}
                handleOpenVenueDetail={handleOpenVenueDetail}
                businessTypes={businessTypes}
                usageTypes={usageTypes}
                filters={filters}
              />
            ))}
          </ListContainer>
          <Container pt="12" width={['full', '2/3', '1/2']}>
            <Container
              fullWidth
              display={Container.displays.flex}
              flexDirection={Container.flexDirections.row}
              justifyContent={Container.justifyContentValues.center}>
              <Pagination
                currentPageIndex={currentPage}
                pageAmount={pageAmount}
                slicePagination={slicePagination}
                startIndex={startIndex}
                endIndex={endIndex}
                onStartIndexHandler={prevPaginate}
                onEndIndexHandler={nextPaginate}
                onPageChangeHandler={paginate}
                data-testing-prefix="SpotsListView"
              />
            </Container>
          </Container>
          <Button
            icon={Icon.images.arrowLeft}
            iconPosition={Button.iconPositions.left}
            onClickHandler={() => {
              backToMap()
            }}
            text={translations.tk_spotListBackButton}
            variant={Button.variants.tertiary}
            data-testing="SpotsListView_Button_Back"
          />
        </Container>
      </LayoutContainer>
    </>
  )
}

export default SpotsListView
